/**
 * PostActions — reply / repost-or-quote / react / zap action bar.
 * Matches the Ditto-style icon row shown in the screenshot:
 *   💬  ↩  ❤  ⚡
 *
 * All counts are fetched in a single combined query per event.
 * Publishing uses useNostrPublish; the user must be logged in.
 */
import { useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMyReaction } from '@/hooks/useMyReaction';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { genUserName } from '@/lib/genUserName';
import { useAuthor } from '@/hooks/useAuthor';
import { useEventStats } from '@/hooks/useEventStats';
import {
  MessageCircle,
  Repeat2,
  Heart,
  Zap,
  Quote,
  Send,
  X,
} from 'lucide-react';

// ─── count query ────────────────────────────────────────────────────────────

function useEventCounts(eventId: string, enabled: boolean) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['event-counts', eventId],
    enabled,
    queryFn: async ({ signal }) => {
      const results = await nostr.query(
        [
          { kinds: [1], '#e': [eventId], limit: 100 },
          { kinds: [6], '#e': [eventId], limit: 100 },
          { kinds: [7], '#e': [eventId], limit: 200 },
          { kinds: [9735], '#e': [eventId], limit: 100 },
        ],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) }
      );
      const replies = results.filter(e => e.kind === 1);
      const reposts = results.filter(e => e.kind === 6);
      const reactions = results.filter(e => e.kind === 7);
      const zaps = results.filter(e => e.kind === 9735);

      // Sum sats from bolt11 tags in zap receipts
      let totalSats = 0;
      for (const zap of zaps) {
        try {
          const bolt11 = zap.tags.find(([t]) => t === 'bolt11')?.[1];
          if (!bolt11) continue;
          const match = bolt11.match(/lnbc(\d+)([munp])/i);
          if (!match) continue;
          const num = parseInt(match[1]);
          const unit = match[2].toLowerCase();
          const msats = unit === 'm' ? num * 100000000
            : unit === 'u' ? num * 100000
            : unit === 'n' ? num * 100
            : num * 100;
          totalSats += Math.round(msats / 1000);
        } catch { /* skip */ }
      }

      return {
        replyCount: replies.length,
        repostCount: reposts.length,
        reactionCount: reactions.length,
        zapSats: totalSats,
        zapCount: zaps.length,
      };
    },
    staleTime: 30000,
  });
}

// ─── Reply dialog ────────────────────────────────────────────────────────────

function ReplyDialog({
  event,
  open,
  onClose,
}: {
  event: NostrEvent;
  open: boolean;
  onClose: () => void;
}) {
  const { user, metadata: myMeta } = useCurrentUser();
  const author = useAuthor(event.pubkey);
  const authorMeta = author.data?.metadata;
  const authorName = authorMeta?.name ?? authorMeta?.display_name ?? genUserName(event.pubkey);
  const myName = myMeta?.name ?? myMeta?.display_name ?? (user ? genUserName(user.pubkey) : '');

  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const submit = async () => {
    if (!text.trim() || !user) return;
    setBusy(true);
    try {
      // Build reply tags per NIP-10: root + reply markers
      const rootTag = event.tags.find(([t, , , marker]) => t === 'e' && marker === 'root')
        ?? event.tags.find(([t]) => t === 'e');
      const tags: string[][] = [];
      if (rootTag) {
        tags.push([rootTag[0], rootTag[1], rootTag[2] ?? '', 'root']);
      }
      tags.push(['e', event.id, '', rootTag ? 'reply' : 'root']);
      tags.push(['p', event.pubkey]);

      await publish({ kind: 1, content: text.trim(), tags });
      toast({ title: 'Reply posted' });
      setText('');
      onClose();
      queryClient.invalidateQueries({ queryKey: ['event-counts', event.id] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'event-stats', event.id] });
    } catch {
      toast({ title: 'Failed to post reply', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-0">
          <DialogTitle className="text-sm font-semibold">Reply</DialogTitle>
          <DialogDescription className="sr-only">Write a reply to this post</DialogDescription>
        </DialogHeader>

        {/* Original post preview */}
        <div className="px-4 py-3 border-b border-border/40 bg-muted/20">
          <div className="flex gap-2.5">
            <Avatar className="w-7 h-7 shrink-0">
              <AvatarImage src={authorMeta?.picture} alt={authorName} />
              <AvatarFallback className="text-[9px]">{authorName.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium mb-0.5">{authorName}</p>
              <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
                {event.content.slice(0, 200)}{event.content.length > 200 ? '…' : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Reply compose */}
        <div className="px-4 py-3">
          <div className="flex gap-2.5">
            <Avatar className="w-7 h-7 shrink-0 mt-0.5">
              <AvatarImage src={myMeta?.picture} alt={myName} />
              <AvatarFallback className="text-[9px]">{myName.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Reply to ${authorName}…`}
              className="flex-1 min-h-[80px] text-sm resize-none border-0 bg-transparent focus-visible:ring-0 p-0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
              }}
            />
          </div>
        </div>

        <div className="flex items-center justify-between px-4 pb-4">
          <span className="text-[10px] text-muted-foreground">⌘↵ to send</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-8 gap-1.5"
              onClick={submit}
              disabled={!text.trim() || busy}
            >
              <Send className="w-3.5 h-3.5" />
              Reply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Quote dialog ─────────────────────────────────────────────────────────────

function QuoteDialog({
  event,
  open,
  onClose,
}: {
  event: NostrEvent;
  open: boolean;
  onClose: () => void;
}) {
  const { user, metadata: myMeta } = useCurrentUser();
  const myName = myMeta?.name ?? myMeta?.display_name ?? (user ? genUserName(user.pubkey) : '');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const nevent = (() => {
    try { return nip19.neventEncode({ id: event.id, author: event.pubkey }); }
    catch { return null; }
  })();

  const submit = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const content = text.trim()
        ? `${text.trim()}\n\nnostr:${nevent}`
        : `nostr:${nevent}`;
      await publish({
        kind: 1,
        content,
        tags: [
          ['e', event.id, '', 'mention'],
          ['p', event.pubkey],
          ['q', event.id],
        ],
      });
      toast({ title: 'Quote posted' });
      setText('');
      onClose();
      queryClient.invalidateQueries({ queryKey: ['event-counts', event.id] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'event-stats', event.id] });
    } catch {
      toast({ title: 'Failed to post quote', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-0">
          <DialogTitle className="text-sm font-semibold">Quote post</DialogTitle>
          <DialogDescription className="sr-only">Quote this post with an optional comment</DialogDescription>
        </DialogHeader>
        <div className="px-4 py-3">
          <div className="flex gap-2.5">
            <Avatar className="w-7 h-7 shrink-0 mt-0.5">
              <AvatarImage src={myMeta?.picture} alt={myName} />
              <AvatarFallback className="text-[9px]">{myName.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add a comment… (optional)"
              className="flex-1 min-h-[70px] text-sm resize-none border-0 bg-transparent focus-visible:ring-0 p-0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
              }}
            />
          </div>
          {/* Quoted note preview chip */}
          <div className="mt-2 ml-9 px-2.5 py-2 rounded-lg border border-border/50 bg-muted/30">
            <p className="text-[10px] text-muted-foreground line-clamp-2">
              {event.content.slice(0, 160)}{event.content.length > 160 ? '…' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 pb-4">
          <span className="text-[10px] text-muted-foreground">⌘↵ to send</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button size="sm" className="h-8 gap-1.5" onClick={submit} disabled={busy}>
              <Quote className="w-3.5 h-3.5" />
              Quote
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Repost menu (repost vs quote) ───────────────────────────────────────────

function RepostMenu({
  event,
  open,
  onClose,
}: {
  event: NostrEvent;
  open: boolean;
  onClose: () => void;
}) {
  const { user } = useCurrentUser();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const doRepost = async () => {
    if (!user) return;
    try {
      await publish({
        kind: 6,
        content: JSON.stringify(event),
        tags: [
          ['e', event.id, '', 'mention'],
          ['p', event.pubkey],
        ],
      });
      toast({ title: 'Reposted' });
      onClose();
      queryClient.invalidateQueries({ queryKey: ['event-counts', event.id] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'event-stats', event.id] });
    } catch {
      toast({ title: 'Failed to repost', variant: 'destructive' });
    }
  };

  if (quoteOpen) {
    return (
      <QuoteDialog
        event={event}
        open
        onClose={() => { setQuoteOpen(false); onClose(); }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xs p-0 gap-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Repost options</DialogTitle>
          <DialogDescription>Repost this note directly or quote it with a comment</DialogDescription>
        </DialogHeader>
        <div className="p-2 space-y-1">
          <button
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm font-medium"
            onClick={doRepost}
          >
            <Repeat2 className="w-4 h-4 text-green-500" />
            Repost
          </button>
          <button
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm font-medium"
            onClick={() => setQuoteOpen(true)}
          >
            <Quote className="w-4 h-4 text-blue-500" />
            Quote post
          </button>
          <button
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-sm text-muted-foreground"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main PostActions ─────────────────────────────────────────────────────────

export interface PostActionsProps {
  event: NostrEvent;
  /** When true the action bar renders slightly smaller (for feed cards) */
  compact?: boolean;
}

export function PostActions({ event, compact = false }: PostActionsProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [replyOpen, setReplyOpen] = useState(false);
  const [repostOpen, setRepostOpen] = useState(false);

  // Prefer Ditto's precomputed stats (one batched query for the whole feed);
  // fall back to client-side counting only when Ditto has no assertion yet.
  const { data: dittoStats } = useEventStats(event.id);
  const { data: fallbackCounts } = useEventCounts(event.id, dittoStats === null);
  const counts = dittoStats ?? fallbackCounts;

  // Check if the current user has already reacted — batched across all
  // visible posts via MyReactionsProvider (one round-trip per ~100 events).
  const { data: myReaction } = useMyReaction(event.id);

  const hasLiked = !!myReaction;

  const toggleReaction = async () => {
    if (!user) { toast({ title: 'Log in to react' }); return; }
    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [
          ['e', event.id],
          ['p', event.pubkey],
        ],
      });
      toast({ title: hasLiked ? 'Reaction removed' : '❤️ Liked' });
      queryClient.invalidateQueries({ queryKey: ['event-counts', event.id] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'event-stats', event.id] });
      queryClient.invalidateQueries({ queryKey: ['my-reaction', event.id, user.pubkey] });
    } catch {
      toast({ title: 'Failed to react', variant: 'destructive' });
    }
  };

  const iconSize = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const btnClass = compact
    ? 'h-7 gap-1 px-2 text-[11px] text-muted-foreground hover:text-foreground'
    : 'h-8 gap-1.5 px-2.5 text-xs text-muted-foreground hover:text-foreground';

  const fmt = (n?: number) =>
    n == null || n === 0 ? '' : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
  const fmtSats = (n?: number) =>
    n == null || n === 0 ? '' : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  return (
    <>
      <div className="flex items-center gap-0 mt-2 -ml-1">
        {/* Reply */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`${btnClass} hover:text-blue-500`}
              onClick={(e) => { e.stopPropagation(); setReplyOpen(true); }}
            >
              <MessageCircle className={iconSize} />
              {fmt(counts?.replyCount)}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">Reply</TooltipContent>
        </Tooltip>

        {/* Repost / Quote */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`${btnClass} hover:text-green-500`}
              onClick={(e) => { e.stopPropagation(); setRepostOpen(true); }}
            >
              <Repeat2 className={iconSize} />
              {fmt(counts?.repostCount)}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">Repost / Quote</TooltipContent>
        </Tooltip>

        {/* React / Like */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`${btnClass} ${hasLiked ? 'text-rose-500' : 'hover:text-rose-500'}`}
              onClick={(e) => { e.stopPropagation(); toggleReaction(); }}
            >
              <Heart className={`${iconSize} ${hasLiked ? 'fill-rose-500' : ''}`} />
              {fmt(counts?.reactionCount)}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">{hasLiked ? 'Unlike' : 'Like'}</TooltipContent>
        </Tooltip>

        {/* Zap (display only — no NWC wired here, but shows count) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`${btnClass} hover:text-amber-500`}
              onClick={(e) => e.stopPropagation()}
            >
              <Zap className={iconSize} />
              {fmtSats(counts?.zapSats)}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {counts?.zapCount ? `${counts.zapCount} zap${counts.zapCount !== 1 ? 's' : ''}` : 'Zap'}
          </TooltipContent>
        </Tooltip>
      </div>

      <ReplyDialog event={event} open={replyOpen} onClose={() => setReplyOpen(false)} />
      <RepostMenu event={event} open={repostOpen} onClose={() => setRepostOpen(false)} />
    </>
  );
}
